// Product Form Modal - Add/Edit EOL/EOS Product

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  PrimaryButton,
  DefaultButton,
  Stack,
  Dropdown,
  Label,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import { EditableDatePicker } from '../../../components/EditableDatePicker';
import type { IDropdownOption } from '@fluentui/react';
import type { EndOfLifeProduct, CreateProductRequest } from '../types';
import { useEolEos } from '../contexts';
import { formatDateForApi, parseDateFromApi } from '../utils/formatters';

interface ProductFormModalProps {
  isOpen: boolean;
  product?: EndOfLifeProduct | null;
  onDismiss: () => void;
  onSave: () => void;
}

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  product,
  onDismiss,
  onSave,
}) => {
  const { addProduct, updateProduct, getManufacturers, isLoading } = useEolEos();

  const [manufactuerName, setManufactuerName] = useState('');
  const [productName, setProductName] = useState('');
  const [productNumber, setProductNumber] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [endOfSalesAnnouncement, setEndOfSalesAnnouncement] = useState('');
  const [endOfSaleDate, setEndOfSaleDate] = useState<Date | null>(null);
  const [endOfSupportDate, setEndOfSupportDate] = useState<Date | null>(null);
  const [lastSupportedDate, setLastSupportedDate] = useState<Date | null>(null);
  const [manufacturerOptions, setManufacturerOptions] = useState<IDropdownOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Load manufacturers on mount
  useEffect(() => {
    const loadManufacturers = async () => {
      const manufacturers = await getManufacturers();
      if (manufacturers) {
        const options: IDropdownOption[] = manufacturers.map((name) => ({
          key: name,
          text: name,
        }));
        setManufacturerOptions(options);
      }
    };
    void loadManufacturers();
  }, [getManufacturers]);

  // Initialize form when product changes
  useEffect(() => {
    if (product) {
      setManufactuerName(product.manufactuerName || '');
      setProductName(product.productName || '');
      setProductNumber(product.productNumber || '');
      setProductDescription(product.productDescription || '');
      setEndOfSalesAnnouncement(product.endOfSalesAnnouncement || '');
      setEndOfSaleDate(parseDateFromApi(product.endOfSaleDate));
      setEndOfSupportDate(parseDateFromApi(product.endOfSupportDate));
      setLastSupportedDate(parseDateFromApi(product.lastSupportedDate));
    } else {
      // Clear form for new product
      setManufactuerName('');
      setProductName('');
      setProductNumber('');
      setProductDescription('');
      setEndOfSalesAnnouncement('');
      setEndOfSaleDate(null);
      setEndOfSupportDate(null);
      setLastSupportedDate(null);
    }
    setErrorMessage('');
  }, [product]);

  const handleSave = async () => {
    // Validation
    if (!manufactuerName || !productName) {
      setErrorMessage('Manufacturer and Product Name are required.');
      return;
    }

    const productData: CreateProductRequest = {
      manufactuerName,
      productName,
      productNumber: productNumber || undefined,
      productDescription: productDescription || undefined,
      endOfSalesAnnouncement: endOfSalesAnnouncement || undefined,
      endOfSaleDate: formatDateForApi(endOfSaleDate),
      endOfSupportDate: formatDateForApi(endOfSupportDate),
      lastSupportedDate: formatDateForApi(lastSupportedDate),
    };

    let result;
    if (product?.id) {
      // Update existing product
      result = await updateProduct(product.id, { ...productData, id: product.id });
    } else {
      // Create new product
      result = await addProduct(productData);
    }

    if (result) {
      onSave();
      onDismiss();
    } else {
      setErrorMessage('Failed to save product. Please try again.');
    }
  };

  const handleCancel = () => {
    setErrorMessage('');
    onDismiss();
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleCancel}
      dialogContentProps={{
        type: DialogType.normal,
        title: product ? 'Edit Product' : 'Add New Product',
      }}
      modalProps={{
        isBlocking: true,
        styles: { main: { maxWidth: 600 } },
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        {errorMessage && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage('')}>
            {errorMessage}
          </MessageBar>
        )}

        <Dropdown
          label="Manufacturer"
          required
          placeholder="Select manufacturer"
          options={manufacturerOptions}
          selectedKey={manufactuerName}
          onChange={(_e, option) => setManufactuerName(option?.key as string || '')}
        />

        <TextField
          label="Product Name"
          required
          value={productName}
          onChange={(_e, newValue) => setProductName(newValue || '')}
        />

        <TextField
          label="Product Number"
          value={productNumber}
          onChange={(_e, newValue) => setProductNumber(newValue || '')}
        />

        <TextField
          label="Product Description"
          multiline
          rows={3}
          value={productDescription}
          onChange={(_e, newValue) => setProductDescription(newValue || '')}
        />

        <TextField
          label="End of Sales Announcement"
          value={endOfSalesAnnouncement}
          onChange={(_e, newValue) => setEndOfSalesAnnouncement(newValue || '')}
        />

        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <Stack.Item grow>
            <Label>End of Sale Date</Label>
            <EditableDatePicker
              placeholder="Select date"
              value={endOfSaleDate || undefined}
              onSelectDate={(date) => setEndOfSaleDate(date || null)}
            />
          </Stack.Item>
          <Stack.Item grow>
            <Label>End of Support Date</Label>
            <EditableDatePicker
              placeholder="Select date"
              value={endOfSupportDate || undefined}
              onSelectDate={(date) => setEndOfSupportDate(date || null)}
            />
          </Stack.Item>
        </Stack>

        <Label>Last Supported Date</Label>
        <EditableDatePicker
          placeholder="Select date"
          value={lastSupportedDate || undefined}
          onSelectDate={(date) => setLastSupportedDate(date || null)}
        />
      </Stack>

      <DialogFooter>
        <PrimaryButton onClick={handleSave} text="Save" disabled={isLoading} />
        <DefaultButton onClick={handleCancel} text="Cancel" disabled={isLoading} />
      </DialogFooter>
    </Dialog>
  );
};
